import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

export function useWebSocket(url = null, autoConnect = false, clientType = 'general') {
  const [connectionStatus, setConnectionStatus] = useState('offline'); // 'offline', 'authenticating', 'connected', 'error'
  const [lastMessage, setLastMessage] = useState(null);
  const [shouldConnect, setShouldConnect] = useState(autoConnect);
  const [websocketId, setWebsocketId] = useState(() => {
    // Load persistent WebSocket ID from localStorage
    const storageKey = `ws_${clientType}_websocket_id`;
    return localStorage.getItem(storageKey) || null;
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  
  const connect = useCallback(() => {
    if (!token || !user) return;
    
    // Prevent multiple simultaneous connections
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      return;
    }
    
    // Determine WebSocket URL based on environment
    let wsUrl;
    if (url) {
      wsUrl = url;
    } else if (import.meta.env.VITE_AMPT_URL) {
      // When AMPT_URL is available (production or Ampt dev), use it with wss://
      wsUrl = import.meta.env.VITE_AMPT_URL.replace('https://', 'wss://');
    } else if (import.meta.env.DEV) {
      // In local development, proxy through /ws path
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
    } else {
      // Fallback to default behavior
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    }
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        // eslint-disable-next-line no-console
        // console.log('WebSocket connected - waiting for server welcome');
        setConnectionStatus('authenticating');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle server welcome - now send authentication/subscription
          if (message.type === 'connected') {
            // eslint-disable-next-line no-console
            // console.log('Received server welcome - sending subscription with token');

            const channels = clientType === 'dashboard'
              ? ['global', `dashboard-${websocketId || 'new'}`]
              : ['global'];

            const subscribeMessage = {
              type: 'subscribe',
              token: `Bearer ${token}`,
              channels,
              clientType,
            };

            // Include websocketId for reconnection if available
            if (websocketId) {
              subscribeMessage.websocketId = websocketId;
              // eslint-disable-next-line no-console
              // console.log(`Reconnecting with existing websocketId: ${websocketId}`);
            } else {
              // eslint-disable-next-line no-console
              // console.log('Creating new persistent connection');
            }

            wsRef.current.send(JSON.stringify(subscribeMessage));
          }

          // Handle successful subscription - token was valid
          if (message.type === 'subscribed') {
            const { websocketId: newWebsocketId } = message;

            // Store WebSocket ID for future reconnections
            if (newWebsocketId) {
              setWebsocketId(newWebsocketId);
              const storageKey = `ws_${clientType}_websocket_id`;
              localStorage.setItem(storageKey, newWebsocketId);
              // eslint-disable-next-line no-console
              // console.log(`Stored ${clientType} websocketId: ${newWebsocketId}`);
            }

            // Token was valid - we're fully connected
            setConnectionStatus('connected');
            // eslint-disable-next-line no-console
            // console.log('✅ WebSocket authenticated and subscribed successfully');

            // Start ping interval to keep connection alive
            // Clear any existing interval first
            if (pingIntervalRef.current) {
              clearInterval(pingIntervalRef.current);
              pingIntervalRef.current = null;
            }

            // Ping every 45 seconds, but only if connection has been idle for 30+ seconds
            pingIntervalRef.current = setInterval(() => {
              const timeSinceLastActivity = Date.now() - lastActivityRef.current;
              const IDLE_THRESHOLD = 30 * 1000; // 30 seconds

              if (timeSinceLastActivity >= IDLE_THRESHOLD && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                // eslint-disable-next-line no-console
                // console.log('📡 Sending WebSocket ping (connection idle)');
                const pingMessage = {
                  type: 'ping',
                  token: `Bearer ${token}`
                };
                wsRef.current.send(JSON.stringify(pingMessage));
              }
            }, 45 * 1000); // 45 seconds
          }

          // Handle authentication/token errors
          if (message.type === 'error') {
            if (message.message.includes('token') || message.message.includes('Authentication')) {
              setConnectionStatus('error');
            }
          }

          // Update last activity timestamp for any message
          lastActivityRef.current = Date.now();
          setLastMessage(message);
        } catch {
          setConnectionStatus('error');
        }
      };

      wsRef.current.onerror = () => {
        setConnectionStatus('error');
      };

      wsRef.current.onclose = (event) => {
        // eslint-disable-next-line no-console
        // console.log('WebSocket disconnected', event.code, event.reason);
        setConnectionStatus('offline');

        // Stop ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Only reconnect if it wasn't a normal closure and we should be connected
        if (event.code !== 1000 && event.code !== 1001 && shouldConnect) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };
    } catch {
      setConnectionStatus('error');
    }
  }, [token, user, url, shouldConnect, clientType, websocketId]);

  const disconnect = useCallback(() => {
    setShouldConnect(false);

    // Stop ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Close with normal closure status code (1000) and reason
      wsRef.current.close(1000, 'Normal closure');
      wsRef.current = null;
    }
  }, []);
  
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Include token with every message for authentication
      const messageWithAuth = {
        ...message,
        token: `Bearer ${token}`
      };
      wsRef.current.send(JSON.stringify(messageWithAuth));
      // Update activity timestamp for outgoing messages too
      lastActivityRef.current = Date.now();
    }
  }, [token]);
  
  const toggleConnection = useCallback(() => {
    if (connectionStatus === 'connected') {
      disconnect();
    } else {
      setShouldConnect(true);
    }
  }, [connectionStatus, disconnect]);

  useEffect(() => {
    if (!shouldConnect) {
      // If shouldConnect becomes false, disconnect
      disconnect();
      return;
    }

    // Delay connection to avoid React StrictMode double-mount issues
    const connectionTimeout = setTimeout(() => {
      connect();
    }, 100);

    return () => {
      clearTimeout(connectionTimeout);
      // Clean up on unmount - disconnect is handled by shouldConnect becoming false
    };
  }, [shouldConnect, connect, disconnect]);
  
  return {
    connectionStatus, // 'offline', 'authenticating', 'connected', 'error'
    isConnected: connectionStatus === 'connected', // For backward compatibility
    lastMessage,
    sendMessage,
    toggleConnection,
    shouldConnect,
    websocketId,
  };
}